import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPatients } from '../../../store/slices/patientsSlice';
import { fetchDoctorOPDAssessment, updateDoctorOPDAssessment } from '../../../store/slices/doctorOPDAssessmentsSlice';
import MainLayout from '../../../components/layout/MainLayout';
import Breadcrumbs from '../../../components/common/Breadcrumbs';
import DoctorOPDAssessmentForm from './DoctorOPDAssessmentForm';
import Swal from 'sweetalert2';

const EditDoctorOPDAssessment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const { patients, isLoading: isPatientsLoading } = useSelector((state) => state.patients);
    const { currentDoctorOPDAssessment, isLoading: isAssessmentLoading } = useSelector((state) => state.doctorOPDAssessments);

    useEffect(() => {
        dispatch(fetchPatients());
        dispatch(fetchDoctorOPDAssessment(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (currentDoctorOPDAssessment && currentDoctorOPDAssessment.id === Number(id)) {
            // Map flattened reason_* fields back to reason_for_admission object
            const reason_for_admission = {
                acute_care: !!currentDoctorOPDAssessment.reason_acute_care,
                relapse: !!currentDoctorOPDAssessment.reason_relapse,
                readmission_medical: !!currentDoctorOPDAssessment.reason_readmission_medical,
                respite_care: !!currentDoctorOPDAssessment.reason_respite_care,
                diagnostic_evaluation: !!currentDoctorOPDAssessment.reason_diagnostic_evaluation,
                for_rehab: !!currentDoctorOPDAssessment.reason_for_rehab,
                clozapine_treatment: !!currentDoctorOPDAssessment.reason_clozapine_treatment,
                chronic_management: !!currentDoctorOPDAssessment.reason_chronic_management,
            };

            setInitialData({
                ...currentDoctorOPDAssessment,
                reason_for_admission,
                labs_investigations: currentDoctorOPDAssessment.labs || [],
            });
        }
    }, [currentDoctorOPDAssessment, id]);

    const handleFormSubmit = async (formData) => {
        setIsLoading(true);
        try {
            await dispatch(updateDoctorOPDAssessment({ id, assessmentData: formData })).unwrap();

            Swal.fire({
                title: 'Success!',
                text: 'Doctor Assessment updated successfully!',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
                position: 'top',
                width: '300px',
            }).then(() => {
                navigate('/doctor-opd-assessments');
            });
        } catch (error) {
            console.error('Error updating assessment:', error);
            Swal.fire({
                title: 'Error!',
                text: typeof error === 'string' ? error : (error.message || 'Failed to update assessment.'),
                icon: 'error',
                confirmButtonText: 'OK',
                position: 'top',
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card mb-4 mt-2">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="mb-0"></h4>
                        <div>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => window.open(`/doctor-opd-assessments/print/${id}`, '_blank')}
                            >
                                <i className="ri-printer-line me-1"></i> Print
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {initialData ? (
                            <DoctorOPDAssessmentForm
                                initialData={initialData}
                                onSubmit={handleFormSubmit}
                                isLoading={isLoading || isPatientsLoading}
                                buttonText="Update Assessment"
                                patients={patients}
                            />
                        ) : (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditDoctorOPDAssessment;
